import { useEffect, useRef } from 'react'
import { Check, ClipboardCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  AGREEMENT_SECTION_KEYS,
  type AgreementSectionKey,
} from '@/lib/agreement-section-completion'

export type AgreementWizardView = AgreementSectionKey | 'review'

interface AgreementSectionStepperProps {
  activeView: AgreementWizardView
  completedSections: Record<AgreementSectionKey, boolean>
  onSelect: (section: AgreementSectionKey) => void
  onSelectReview: () => void
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

/** Chip strip below 1280px, side rail from 1280px. Every chip jumps straight to its view. */
export function AgreementSectionStepper({
  activeView,
  completedSections,
  onSelect,
  onSelectReview,
}: AgreementSectionStepperProps) {
  const { t } = useTranslation()
  const trackRef = useRef<HTMLDivElement>(null)

  // Keep the current chip visible in the strip. Horizontal only, so the window scroll
  // that step navigation sets is never disturbed.
  useEffect(() => {
    const track = trackRef.current
    const chip = track?.querySelector<HTMLButtonElement>(`[data-view="${activeView}"]`)
    if (!track || !chip || track.scrollWidth <= track.clientWidth) return
    const left = chip.offsetLeft - track.offsetLeft - 16
    track.scrollTo({ left: Math.max(0, left), behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
  }, [activeView])

  const reviewActive = activeView === 'review'

  return (
    <nav className="prod-agreement-stepper" aria-label={t('agreement.sectionNav')}>
      <div ref={trackRef} className="prod-agreement-stepper__track">
        {AGREEMENT_SECTION_KEYS.map((key, index) => {
          const fullTitle = t(`form.sections.${key}`)
          const shortLabel = t(`form.sectionsShort.${key}`)
          const isActive = activeView === key
          const isComplete = completedSections[key]
          const statusLabel = isComplete ? t('form.sectionComplete') : t('form.sectionIncomplete')

          return (
            <button
              key={key}
              type="button"
              data-view={key}
              className={[
                'prod-agreement-stepper__step',
                isActive ? 'prod-agreement-stepper__step--current' : '',
                isComplete ? 'prod-agreement-stepper__step--complete' : 'prod-agreement-stepper__step--incomplete',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-current={isActive ? 'step' : undefined}
              aria-label={`${fullTitle}, ${statusLabel}`}
              title={fullTitle}
              onClick={() => onSelect(key)}
            >
              <span className="prod-agreement-stepper__marker" aria-hidden="true">
                {isComplete ? (
                  <Check className="prod-agreement-stepper__check" strokeWidth={3} />
                ) : (
                  String(index + 1).padStart(2, '0')
                )}
              </span>
              <span className="prod-agreement-stepper__label">{shortLabel}</span>
            </button>
          )
        })}
        <button
          type="button"
          data-view="review"
          className={[
            'prod-agreement-stepper__step',
            'prod-agreement-stepper__step--review',
            reviewActive ? 'prod-agreement-stepper__step--current' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-current={reviewActive ? 'step' : undefined}
          onClick={onSelectReview}
        >
          <span className="prod-agreement-stepper__marker" aria-hidden="true">
            <ClipboardCheck className="prod-agreement-stepper__check" />
          </span>
          <span className="prod-agreement-stepper__label">{t('agreement.reviewShort')}</span>
        </button>
      </div>
    </nav>
  )
}
